import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Routes } from '../../../../app/routes';
import Modal from '../../../../shared/components/modal/modal';
import { handleServerFormErrors } from '../../../../shared/utils/error-handling/error-handling';
import { useAppSelector } from '../../../../store/hooks';
import { requestPreviewAlertId } from '../../../request/constants/constants';
import { setActiveRequestId } from '../../../request/store/requests-slice';
import LazyModal, { LazyModalComponentProps } from '../../../ui/components/lazy-modal';
import { closeModal, setAlert } from '../../../ui/store/ui-slice';
import { useAttachScanRequestMutation } from '../../api/scan-requests-api';
import { scanRequestAlertId, scanRequestMoveModalId } from '../../constants/constants';
import { ScanRequestMoveFormModel } from '../../models/scan-request-move-form-model';
import {
  selectActiveScanRequestId,
  selectTempScanRequestId,
  setTempScanRequestId,
} from '../../store/scan-requests-slice';

const ScanRequestMoveFormContent = ({ isOpen, afterClose }: LazyModalComponentProps): JSX.Element => {
  const { t } = useTranslation(['common', 'scan-requests']);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tempScanRequestId = useAppSelector(selectTempScanRequestId);
  const activeScanRequestId = useAppSelector(selectActiveScanRequestId);
  const [form] = Form.useForm<ScanRequestMoveFormModel>();
  const [attachRequest, { isLoading }] = useAttachScanRequestMutation();

  const closeFormModal = () => {
    dispatch(closeModal(scanRequestMoveModalId));
    dispatch(setTempScanRequestId(null));
  };

  const setScanRequestMoveAlertMessage = (alertId: string) => {
    dispatch(
      setAlert({
        id: alertId,
        type: 'success',
        title: t('signScanRequest.message.requestMoved'),
      }),
    );
  };

  const submitForm = (formValues: ScanRequestMoveFormModel) => {
    if (!tempScanRequestId?.length) {
      return;
    }

    attachRequest({
      scanRequestId: tempScanRequestId,
      ...formValues,
    })
      .unwrap()
      .then(response => {
        closeFormModal();

        if (tempScanRequestId === activeScanRequestId) {
          dispatch(setActiveRequestId(response.request));
        }
        setScanRequestMoveAlertMessage(scanRequestAlertId);
        setScanRequestMoveAlertMessage(requestPreviewAlertId);
        navigate(Routes.ScanRequests);
      })
      .catch(error => {
        handleServerFormErrors(
          form,
          error.data?.errors || {
            shortId: error.data?.ErrorMessage
              ? [error.data.ErrorMessage]
              : [t('signScanRequest.validationError.errorOccurred')],
          },
        );
      });
  };

  return (
    <Modal
      title={t('moveRequestFormModal.title', { ns: 'scan-requests' })}
      onCancel={closeFormModal}
      isLoading={isLoading}
      customSubmitText={t('action.submit')}
      isOpen={isOpen}
      formId={scanRequestMoveModalId}
      afterClose={afterClose}
    >
      <Form id={scanRequestMoveModalId} form={form} layout="vertical" onFinish={submitForm}>
        <Form.Item
          name="shortId"
          label={t('signScanRequest.label.shortId')}
          rules={[{ required: true, message: t('signScanRequest.validationError.requestRequired') }]}
        >
          <Input placeholder={t('signScanRequest.label.shortId')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const ScanRequestMoveFormModal = (): JSX.Element => {
  return <LazyModal modalId={scanRequestMoveModalId} Component={ScanRequestMoveFormContent} />;
};

export default ScanRequestMoveFormModal;
